/**
 * Centralized readiness logic for car listings.
 *
 * "Essentials complete" means a draft has the four core sections from the
 * readiness checklist filled in: Vehicle Details, Description, Location and
 * Photos. Once these are complete a host should be steered to the tabbed edit
 * view rather than the step-by-step wizard, since only Pricing / Availability
 * (and optionally Rules) typically remain.
 *
 * The Description threshold here (>= 10 chars) is the single source of truth
 * for "has a description" across the host UI — both this helper and the Status
 * tab's Readiness checklist consume it. The backend "submit" endpoint still
 * enforces the 100-char minimum before the listing can go live.
 */

import { PHOTO_MIN, computeSuggestedPricing } from "@workspace/listing-requirements";

export interface ListingEssentials {
  make?: string | null;
  model?: string | null;
  year?: number | string | null;
  descriptionLength?: number | null;
  city?: string | null;
  countryCode?: string | null;
  photoCount?: number | null;
}

const MIN_DESCRIPTION_LENGTH = 10;

export function hasEssentialVehicle(e: ListingEssentials): boolean {
  return !!(e.make && e.model && e.year);
}

export function hasEssentialDescription(e: ListingEssentials): boolean {
  return (e.descriptionLength ?? 0) >= MIN_DESCRIPTION_LENGTH;
}

export function hasEssentialLocation(e: ListingEssentials): boolean {
  return !!(e.city && e.countryCode);
}

/**
 * Photos readiness threshold mirrors the wizard's own Next gate
 * (`PHOTO_MIN` from `@workspace/listing-requirements`, currently 5). The
 * resume inference (`inferResumeWizardStep`), the per-step
 * `wizardStepNeedsInput` check, the Status tab readiness checklist
 * (`isEssentialsComplete`), and the wizard's own `validateForWizardSubmit`
 * must all agree on what "photos done" means — otherwise a draft with 1–4
 * photos passes the resume check and the wizard skips past the Photos step
 * even though the host still has the "X more photo(s) needed" hint visible
 * (task #1650).
 */
export function hasEssentialPhotos(e: ListingEssentials): boolean {
  return (e.photoCount ?? 0) >= PHOTO_MIN;
}

/**
 * True when all four "essentials" readiness items are complete.
 * Used to decide whether to route a draft into the tabbed edit view
 * instead of the step-by-step wizard.
 */
export function isEssentialsComplete(e: ListingEssentials): boolean {
  return (
    hasEssentialVehicle(e) &&
    hasEssentialDescription(e) &&
    hasEssentialLocation(e) &&
    hasEssentialPhotos(e)
  );
}

/**
 * Per-wizard-step completeness signals used by the listing-wizard "Continue"
 * resume path (task #1473). These mirror the validation a host has to clear
 * to move forward from each content step in the wizard, so the resume
 * inference and the readiness checklist stay in agreement.
 */
export interface ListingResumeFields extends ListingEssentials {
  /** Server-owned choice requirement. `undefined` is fail-closed for US drafts. */
  valuationChoiceRequired?: boolean | null;
  colour?: string | null;
  mileage?: number | string | null;
  condition?: number | string | null;
  rental_driving_active?: boolean | null;
  price_driving?: number | string | null;
  rental_chauffeured_active?: boolean | null;
  chauffeured_hourly_rate?: number | string | null;
  chauffeured_min_price_hourly_rate?: number | string | null;
  rental_event_active?: boolean | null;
  price_event?: number | string | null;
  event_hourly_rate?: number | string | null;
  /**
   * Insured / market value of the vehicle in the local currency. Combined
   * with `countryCode` it lets the readiness signal compute the same
   * per-currency chauffeured hourly floor (`minHourlyChauffeured`) the
   * publish-time validator in `lib/listing-requirements/src/validation.ts`
   * enforces — so a draft whose chauffeured hourly rate is positive but
   * below the floor is correctly steered back to Pricing on resume rather
   * than skipped past to Review & submit (task #1761). When this is
   * missing we fall back to the positive-only check so undated drafts
   * aren't trapped.
   */
  insuredValue?: number | string | null;
}

const isFilled = (v: unknown): boolean => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (typeof v === "number") return Number.isFinite(v) && v > 0;
  return true;
};

export function hasColourPickerEssentials(d: ListingResumeFields): boolean {
  return isFilled(d.colour) && isFilled(d.mileage) && isFilled(d.condition);
}

/**
 * Stricter than `isFilled` for prices: a string "0" must NOT count as a real
 * price. The wizard stores numeric inputs as strings, so this is the same
 * `Number(x) > 0` semantic the readiness checklist used to inline in
 * `TabStatus.tsx` before task #1511 collapsed both call sites onto this
 * predicate.
 */
const isPositivePrice = (v: unknown): boolean => {
  if (v === null || v === undefined) return false;
  if (typeof v === "number") return Number.isFinite(v) && v > 0;
  if (typeof v === "string") {
    if (v.trim() === "") return false;
    const n = Number(v);
    return Number.isFinite(n) && n > 0;
  }
  return false;
};

/**
 * True when the chauffeured hourly rate clears the per-currency hard
 * minimum the publish-time validator (`validateConditional`) enforces. We
 * compute the floor from the same `computeSuggestedPricing` the validator
 * uses so the two cannot drift (task #1761). When valuation data isn't
 * available yet we fall back to the positive-only check so we don't
 * accidentally trap drafts that simply haven't been valued — the resume
 * inference would otherwise have nowhere correct to send them.
 */
function chauffeuredHourlyClearsFloor(d: ListingResumeFields): boolean {
  const hr = Number(d.chauffeured_hourly_rate);
  if (!Number.isFinite(hr) || hr <= 0) return false;
  const insured = d.insuredValue == null ? NaN : Number(d.insuredValue);
  if (!Number.isFinite(insured) || insured <= 0) {
    // No valuation yet — fall back to the legacy positive-only behaviour.
    return true;
  }
  const pricing = computeSuggestedPricing({
    insuredValue: insured,
    countryCode: d.countryCode ?? null,
  });
  return hr >= pricing.minHourlyChauffeured;
}

/**
 * True when a draft satisfies pricing for at least one of its active rental
 * types. Single source of truth for the wizard-resume inference, the
 * readiness checklist on the Status tab, and the auto-submit gate. See
 * task #1511 for why these have to agree.
 *
 * Chauffeured hire additionally requires the hourly rate to clear the
 * per-currency `minHourlyChauffeured` floor the publish-time validator
 * enforces — see `chauffeuredHourlyClearsFloor` (task #1761).
 */
export function hasPricingEssentials(d: ListingResumeFields): boolean {
  if (!d.rental_driving_active && !d.rental_chauffeured_active && !d.rental_event_active) return false;
  if (d.rental_driving_active && !isPositivePrice(d.price_driving)) return false;
  if (d.rental_event_active && !isPositivePrice(d.price_event)) return false;
  if (d.rental_chauffeured_active && (
    !isPositivePrice(d.chauffeured_hourly_rate) ||
    !isPositivePrice(d.chauffeured_min_price_hourly_rate) ||
    !chauffeuredHourlyClearsFloor(d) ||
    Number(d.chauffeured_min_price_hourly_rate) < Number(d.chauffeured_hourly_rate)
  )) return false;
  return true;
}

/**
 * Wizard step IDs in resume order. Mirrors the content steps of the
 * `VISUAL_STEPS` list in `ListingWizardPage.tsx`, in the same order. Kept as
 * a tiny shared list so the resume inference and the wizard agree.
 */
export const RESUME_STEP_ORDER = [
  "vehicle-identity",
  "colour-picker",
  "car-location",
  "valuation-approval",
  "car-story",
  "photos",
  "pricing",
  "preview-submit",
] as const;

export type ResumeStepId = typeof RESUME_STEP_ORDER[number];

/**
 * US listings created through the native onboarding flow require an explicit
 * host decision; an estimate by itself is never consent. The API sends false
 * for historical/imported listings that are outside that flow. If the flag is
 * missing, keep US drafts fail-closed while older clients roll forward.
 */
export function needsHostValuationChoice(
  countryCode: string | null | undefined,
  choiceRequired: boolean | null | undefined,
): boolean {
  return (countryCode || "").toUpperCase() === "US" && choiceRequired !== false;
}

/**
 * Returns the wizard step ID a draft should resume on based purely on which
 * required fields are filled. Walks the content steps in order and picks the
 * first one whose required input is missing. Falls back to `preview-submit`
 * when every required input is in.
 */
export function inferResumeWizardStep(d: ListingResumeFields): ResumeStepId {
  if (!hasEssentialVehicle(d)) return "vehicle-identity";
  if (!hasColourPickerEssentials(d)) return "colour-picker";
  if (!hasEssentialLocation(d)) return "car-location";
  if (needsHostValuationChoice(d.countryCode, d.valuationChoiceRequired)) return "valuation-approval";
  if (!hasEssentialDescription(d)) return "car-story";
  if (!hasEssentialPhotos(d)) return "photos";
  if (!hasPricingEssentials(d)) return "pricing";
  return "preview-submit";
}

/**
 * True when a wizard content step still has unfinished required input. Used
 * by the resume-path skip-forward logic so the wizard never lands a host on
 * a content step they've already filled (after we've already skipped past
 * non-input screens like phase-intros and the enrichment/apex screens).
 */
export function wizardStepNeedsInput(stepId: ResumeStepId, d: ListingResumeFields): boolean {
  switch (stepId) {
    case "vehicle-identity":
      return !hasEssentialVehicle(d);
    case "colour-picker":
      return !hasColourPickerEssentials(d);
    case "car-location":
      return !hasEssentialLocation(d);
    case "car-story":
      return !hasEssentialDescription(d);
    case "photos":
      return !hasEssentialPhotos(d);
    case "pricing":
      return !hasPricingEssentials(d);
    case "valuation-approval":
      return needsHostValuationChoice(d.countryCode, d.valuationChoiceRequired);
    case "preview-submit":
      return true;
  }
}

/**
 * True when a listing is in the host-editable "draft" lifecycle state.
 *
 * Both canonical pre-submission states (`incomplete` and `ready_to_submit`)
 * count as draft for routing purposes — the readiness checklist surfaces the
 * difference. Anything past submission (awaiting_approval, active, etc.) is
 * not a draft.
 */
export function isDraftStatus(status: string | null | undefined): boolean {
  return status === "incomplete" || status === "ready_to_submit";
}
